import { useEffect, useMemo, useState } from "react";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import {
  Alert,
  Backdrop,
  Box,
  Card,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import { handleAxiosError } from "app/services/app.error.handler";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { MANAGER_OPTION_PREFIX, mergeManagerScope } from "utils/managerSubtree";
import { MEMBER_VALUE_MEMBER_ID } from "utils/effortUserFilter";
import { EffortFilterBar } from "app/components/common/EffortFilterBar";
import { ReportFormatSelect } from "app/components/common/ReportFormatSelect";
import { defaultPeriod, periodRange } from "app/components/common/PeriodFilter";
import { useManagerScopeOptions } from "app/hooks/useManagerScopeOptions";
import { useUserFilterOptions } from "app/hooks/useUserFilterOptions";
import {
  downloadConsolidatedTimesheetHandler,
  downloadConciseMonthlyHandler,
  downloadTimesheetHandler,
  downloadUtilizationReportHandler,
  loadMembersForAllClientsFromBackend,
  loadMembersFromBackendForClient,
} from "app/services/member.service";
import {
  loadScopeManagersForIds,
  loadMembersForManagerCached,
  loadAllManagersFromBackend,
} from "app/services/manager.service";
import { loadAllClients } from "app/services/client.service";
import { loadAllDepartments } from "app/services/department.service";
import { ClientFilterSelect } from "app/components/common/ClientFilterSelect";
import { toClientListOptions } from "utils/clientOptions";
import {
  checkUserHasAccountRole,
  checkUserHasAdminRole,
  checkUserHasBdmRole,
  checkUserHasManagerRole,
  checkUserHasMemberRole,
  getBdmClients,
  getCurrentManager,
  getMemberClients,
  readSessionCurrentManager,
} from "app/services/app.state.service";
import { ALL_CLIENTS, MANAGER_SELF_OPTION_VALUE, OWNER_TYPE_MEMBER, OWNER_TYPE_MANAGER } from "AppConstants";
import { useDispatch, useSelector } from "react-redux";

// Export formats. "detailed" is the current multi-column layout; "legacy" is the classic
// single-column layout. Removing the legacy format = drop the "legacy" option below.
const FORMAT_DETAILED = "detailed";
const FORMAT_LEGACY = "legacy";
// "consolidated" is one workbook covering every member in the manager's scope, each a column group.
// Unlike the other two it is scope-driven, not owner-driven, so the person filter does not apply and
// a single client is required -- the layout has no Account column.
// @since NEXT_VERSION
const FORMAT_CONSOLIDATED = "consolidated";
// "conciseMonthly" is HRMS-267's finance report: one member, a row per activity category and a
// column per calendar day. Named for the report rather than "Concise", which the legacy format
// already occupies in this dropdown. Owner-driven and member-only -- the server derives the client
// from the member's reporting record, so unlike Consolidated it needs no client filter.
// @since NEXT_VERSION
const FORMAT_CONCISE_MONTHLY = "conciseMonthly";
// "utilization" is HRMS-319's org-level report: every person x client for one month, admin-only.
// @since NEXT_VERSION
const FORMAT_UTILIZATION = "utilization";
// The shared select's "All" entry carries ALL_CLIENTS; for a department it means the whole org.
// @since NEXT_VERSION
const ALL_DEPARTMENTS = ALL_CLIENTS;
// Departments that log no timesheets, so a report filtered to one would always be empty.
// Matched on display name, case-insensitively. @since NEXT_VERSION
const DEPARTMENTS_WITHOUT_TIMESHEETS = ["operations"];

// Stable empty-cache fallback so the selector doesn't return a fresh ref each render.
const EMPTY_CACHE = {};

// Both download endpoints take a calendar month and year — there is no range parameter — and the
// workbook itself is laid out as one month of weekly sheets. The period picker offers Fiscal Week
// and Custom Dates as well, so those are resolved to the month their range *starts* in. A range
// spanning two months therefore exports the first of them, not both.
// @since NEXT_VERSION
const exportMonthYear = (period) => {
  const start = dayjs(periodRange(period).from);
  return { calendarMonth: start.month() + 1, year: start.year() };
};

const ExportReportsView = () => {
  const dispatch = useDispatch();
  // Session cache keyed by manager_id, read before hitting by-manager/{id}.
  const membersByManager = useSelector((state) => state.client?.membersByManager || EMPTY_CACHE);
  // Full period shape from the start, even though only the month and year are offered — the
  // picker reports the whole value back, so seeding a partial one made the state change shape
  // after the first change.
  const [period, setPeriod] = useState(defaultPeriod);
  const [selectedMember, setSelectedMember] = useState(null);
  const [currentManager, setCurrentManager] = useState(null);
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [exportClientId, setExportClientId] = useState(ALL_CLIENTS);
  const [managerClients, setManagerClients] = useState([]);
  const [format, setFormat] = useState(FORMAT_DETAILED);
  // The logged-in manager's subtree (self + L1 children). Drives the client filter,
  // the manager-scoped member list, and the "Managers" optgroup — mirroring the
  // Consolidated Report. An L1 manager's subtree contains only themselves.
  const [subtreeManagers, setSubtreeManagers] = useState([]);
  const [members, setMembers] = useState([]);
  // Admin only: every manager (L1 + L2) in the system, offered in the "Managers" optgroup.
  // @since NEXT_VERSION
  const [adminManagers, setAdminManagers] = useState([]);
  // Admin only: every client in the system. A manager's client list is derived from their own
  // manager records, which an admin does not have — without a separate source the Client filter
  // would render with no options at all.
  // @since NEXT_VERSION
  const [adminClients, setAdminClients] = useState([]);
  // Admin only: the Consolidated section is a separate card with its own Export button, so it
  // carries its own client and period. Sharing the individual section's state would mean
  // retuning one card's filters silently changed what the other card exported.
  // @since NEXT_VERSION
  const [consolidatedClientId, setConsolidatedClientId] = useState("");
  const [consolidatedPeriod, setConsolidatedPeriod] = useState(defaultPeriod);
  // Which consolidated report to render. One choice exists today (AXTech); it is state rather than
  // a constant so the control is real -- a second consolidated renderer becomes an extra option
  // here and nothing else has to change.
  // @since NEXT_VERSION
  const [consolidatedFormat, setConsolidatedFormat] = useState(FORMAT_CONSOLIDATED);
  // The section shows no person filter, so the bar resolves this to "All" and reports it back
  // (HRMS-260 point 3). Held rather than ignored so the value the screen means is observable
  // instead of implied.
  // @since NEXT_VERSION
  const [consolidatedUserValue, setConsolidatedUserValue] = useState(null);
  // Admin only: the Org-level section's own period and report, so its filters stay independent.
  // @since NEXT_VERSION
  const [orgPeriod, setOrgPeriod] = useState(defaultPeriod);
  const [orgFormat, setOrgFormat] = useState(FORMAT_UTILIZATION);
  const [orgDepartment, setOrgDepartment] = useState(ALL_DEPARTMENTS);
  const [departments, setDepartments] = useState([]);

  // Every member record the logged-in person holds -- one per client. Sourced from
  // getMemberClients rather than getCurrentMember: that call writes SESSION_KEY_CURRENT_MEMBER and
  // SESSION_KEY_CURRENT_CLIENT when the session is cold, so opening this read-only screen would
  // have moved the member's active client underneath them (it shares those keys with
  // switchMemberClient). getMemberClients only fills its own cache.
  // @since NEXT_VERSION
  const [memberships, setMemberships] = useState([]);

  const isManager = checkUserHasManagerRole();
  const isAdmin = checkUserHasAdminRole();
  // A plain member exports their own timesheet and nothing else: no person filter, no
  // Consolidated section and no finance format. ACCOUNT is excluded alongside manager and admin
  // because this screen already treats it as a wide scope (it loads every member in the system
  // below); without that, an ACCOUNT + EMPLOYEE_MEMBER user reached the route on the member role
  // and was then forced into the self-only view while the all-members fetch still ran.
  // @since NEXT_VERSION
  // BDM joins ACCOUNT and the two wider roles in the exclusion list for the same reason: this screen
  // treats it as a wide scope and loads an account list for it below, so a member who is also a BDM
  // takes the BDM path. They lose the self-only view, which is the deliberate trade -- the BDM scope
  // is the wider of the two and still reaches their own records through the person filter.
  // @since NEXT_VERSION
  const isMemberScope =
    checkUserHasMemberRole() && !isManager && !isAdmin && !checkUserHasAccountRole()
    && !checkUserHasBdmRole();
  // Precedence for a dual-role (ADMIN + EMPLOYEE_MANAGER) user is decided explicitly here
  // rather than by whichever member-list request resolves last: admin wins. An admin loads
  // every member in the system and exports anyone, so the manager-subtree data path (subtree
  // load, subtree-scoped members and "(Me)") is switched off for them. The Client filter is
  // not part of that: an admin gets one too, fed from the full client list instead.
  // @since NEXT_VERSION
  const isManagerScope = isManager && !isAdmin;
  // A BDM exports the accounts they are the relationship manager for (HRMS-116 point 7: "Export
  // reports will pretty much work like Managers"). They hold no manager records, so the scope is
  // rooted at the clients naming them rather than at a subtree, and the Consolidated card -- which
  // is manager-scope driven -- stays hidden for them.
  // @since NEXT_VERSION
  const isBdmScope = checkUserHasBdmRole() && !isManager && !isAdmin;
  const [bdmClients, setBdmClients] = useState([]);

  // Populate manager's client list from session on mount. Account and Admin roles skip the
  // manager subtree entirely and load every member in the system for the picker.
  useEffect(() => {
    if (isManagerScope) {
      const list = readSessionCurrentManager();
      setManagerClients(Array.isArray(list) ? list : []);
    }
    if (checkUserHasAccountRole() || isAdmin) {
      loadMembersForAllClientsFromBackend()
        .then((response) => setMembers(response.status === 204 ? [] : response.data))
        .catch((error) => {
          setMembers([]);
          handleAxiosError(error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Admin: active departments for the Org-level Department filter. Failure leaves only "All".
  // @since NEXT_VERSION
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await loadAllDepartments("ACTIVE");
        if (!cancelled) setDepartments(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        if (!cancelled) setDepartments([]);
        handleAxiosError(error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Admin: load every client once, to populate the Client filter. Failure leaves the list empty,
  // which degrades to the previous behaviour (no client scoping) rather than blocking the export.
  // @since NEXT_VERSION
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    loadAllClients()
      .then((response) => {
        if (cancelled) return;
        setAdminClients(
          response.status === 204 || !Array.isArray(response.data) ? [] : response.data
        );
      })
      .catch(() => {
        if (!cancelled) setAdminClients([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Admin: load every manager (L1 + L2) once, to offer as selectable Manager options.
  // The download endpoints accept any manager id, so an admin can export any manager's report.
  // @since NEXT_VERSION
  // A BDM loads the same list. HRMS-116 point 7 -- "Export reports will pretty much work like
  // Managers" -- so a BDM must be able to export the managers at their own accounts. Without this
  // the Member / Manager dropdown offered members only: adminManagers stayed empty for them, and
  // the fallback below reads the manager-subtree list, which a BDM (holding no _manager record)
  // never populates either.
  //
  // The endpoint returns every manager, so the list is narrowed to this BDM's accounts here. That
  // narrowing is a display concern -- ExportAccessPolicy is what actually refuses an export
  // outside their accounts.
  // @since NEXT_VERSION
  useEffect(() => {
    if (!isAdmin && !isBdmScope) return undefined;
    let cancelled = false;
    loadAllManagersFromBackend()
      .then((response) => {
        if (cancelled) return;
        const all = response.status === 204 || !Array.isArray(response.data) ? [] : response.data;
        if (!isBdmScope) {
          setAdminManagers(all);
          return;
        }
        const ownClientIds = new Set(bdmClients.map((c) => String(c.id)));
        setAdminManagers(all.filter((m) => ownClientIds.has(String(m.clientId))));
      })
      .catch(() => {
        if (!cancelled) setAdminManagers([]);
      });
    return () => {
      cancelled = true;
    };
    // bdmClients is a dependency because the narrowing above reads it, and a BDM's account list
    // resolves asynchronously -- filtering against an empty set would drop every manager.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isBdmScope, bdmClients]);

  // Load the subtree from ALL the manager's records, not just the first — an L1 may
  // report through any of the L2's records.
  useEffect(() => {
    if (!isManagerScope) return;
    const managerIds = managerClients.map((c) => c.id).filter((id) => id != null);
    if (managerIds.length === 0) return;
    let cancelled = false;
    loadScopeManagersForIds(managerIds)
      .then((list) => {
        if (!cancelled) setSubtreeManagers(list);
      })
      .catch(() => {
        if (!cancelled) setSubtreeManagers([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managerClients]);

  // Load members whenever the selected client or the subtree changes. Members are
  // aggregated across every manager in scope (the rolled-up subtree plus the manager's
  // own assignments, like the Consolidated Report) so an L2 sees their L1 children's
  // members and an independent assignment's members both show.
  useEffect(() => {
    if (!isManagerScope) return;
    // Wait for the subtree so members load in one pass (not a managerClients-only first
    // pass), then union in the manager's own assignments to cover independent records.
    if (subtreeManagers.length === 0) return;
    const scope = mergeManagerScope(subtreeManagers, managerClients);
    const managersInScope =
      exportClientId === ALL_CLIENTS
        ? scope
        : scope.filter((m) => String(m.clientId) === String(exportClientId));
    if (managersInScope.length === 0) {
      setMembers([]);
      return;
    }
    Promise.all(
      managersInScope.map((m) => loadMembersForManagerCached(m.id, membersByManager, dispatch))
    ).then((lists) => {
      const seen = new Set();
      setMembers(lists.flat().filter((m) => (seen.has(m.id) ? false : seen.add(m.id))));
    });
    // membersByManager is read but omitted from deps: the view writes to it, so
    // depending on it would self-trigger a re-run (client/subtree changes suffice).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportClientId, subtreeManagers, managerClients]);

  // Resolve the logged-in manager for the "(Me)" option
  useEffect(() => {
    if (!isManagerScope) return;
    let cancelled = false;
    getCurrentManager()
      .then((manager) => {
        if (!cancelled) setCurrentManager(manager || null);
      })
      .catch(() => {
        if (!cancelled) setCurrentManager(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve the logged-in member's records. One per client, which is what lets the Client filter
  // below offer the accounts they actually work at and pick the right record for each.
  // @since NEXT_VERSION
  useEffect(() => {
    if (!isMemberScope) return;
    let cancelled = false;
    getMemberClients()
      .then((records) => {
        if (!cancelled) setMemberships(Array.isArray(records) ? records : []);
      })
      .catch(() => {
        if (!cancelled) setMemberships([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A manager defaults to their own "(Me)" report as soon as their record resolves.
  // Members load into the dropdown afterwards as extra options, but must not move the
  // selection off the manager — otherwise the value visibly jumps on load.
  useEffect(() => {
    if (selectedMember == null && currentManager) {
      setSelectedMember(MANAGER_SELF_OPTION_VALUE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentManager]);

  // The clients the manager can export for, and the L1 children they can export on behalf
  // of — both derived from the subtree unioned with their own session assignments.
  const { clientOptions: managerClientOptions, subtreeL1Options: managerL1Options } =
    useManagerScopeOptions({
      subtreeManagers,
      managerClients,
      selectedClient: exportClientId,
    });

  // A manager's clients come from their own subtree; an admin's from the full client list, since
  // they hold no manager records to derive one from.
  // @since NEXT_VERSION
  // A BDM's clients are the accounts naming them as relationship manager, and their member pool is
  // everyone placed on those accounts. Mirrors the data path the BDM members screen used before
  // HRMS-116 dropped that screen.
  // @since NEXT_VERSION
  useEffect(() => {
    if (!isBdmScope) return;
    let cancelled = false;
    getBdmClients()
      .then((clients) => {
        if (cancelled) return;
        const list = Array.isArray(clients) ? clients : [];
        setBdmClients(list);
        const ids = list.map((c) => c.id).filter((id) => id != null);
        if (ids.length === 0) {
          setMembers([]);
          return Promise.resolve([]);
        }
        return Promise.all(
          ids.map((id) =>
            loadMembersFromBackendForClient(id)
              .then((r) => (r.status === 204 || !Array.isArray(r.data) ? [] : r.data))
              .catch((error) => {
                handleAxiosError(error);
                return [];
              })
          )
        ).then((lists) => {
          if (cancelled) return;
          // De-duped by member id: one person can sit on more than one of their accounts.
          const byId = new Map(lists.flat().map((m) => [m.id, m]));
          setMembers([...byId.values()]);
        });
      })
      .catch(() => {
        if (cancelled) return;
        setBdmClients([]);
        setMembers([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A member's clients come from their own memberships, so the filter offers exactly the accounts
  // they work at -- review asked for this so a member can export one client at a time rather than
  // always getting every client in one workbook.
  // @since NEXT_VERSION
  const memberClientOptions = useMemo(
    () => toClientListOptions(memberships.map((m) => m.client).filter(Boolean)),
    [memberships]
  );

  const clientOptions = useMemo(
    () =>
      isAdmin
        ? toClientListOptions(adminClients)
        : isBdmScope
          ? toClientListOptions(bdmClients)
          : isMemberScope
            ? memberClientOptions
            : managerClientOptions,
    [isAdmin, isBdmScope, isMemberScope, adminClients, bdmClients, memberClientOptions,
      managerClientOptions]
  );

  // Options for the "Managers" optgroup. For a manager: their L1 children (own records
  // excluded — "(Me)" covers them; empty for an L1). For an admin: every manager (L1 + L2)
  // in the system, narrowed to the selected client. Both shapes carry
  // { id, name, clientId, clientName, clientCode }.
  // @since NEXT_VERSION
  const subtreeL1Options = useMemo(() => {
    // A BDM reads the same source as an admin -- adminManagers, already narrowed to their own
    // accounts where it is loaded -- rather than the manager-subtree list, which is empty for them.
    if (!isAdmin && !isBdmScope) return managerL1Options;
    if (exportClientId === ALL_CLIENTS) return adminManagers;
    return adminManagers.filter((m) => String(m.clientId) === String(exportClientId));
  }, [isAdmin, isBdmScope, managerL1Options, adminManagers, exportClientId]);

  // The manager path narrows its member list server-side, by refetching per manager in scope.
  // An admin loads every member once and narrows here instead — a refetch would be the same
  // request each time. Each member record names the one client it belongs to.
  // @since NEXT_VERSION
  const scopedMembers = useMemo(() => {
    if (!(isAdmin || isBdmScope) || exportClientId === ALL_CLIENTS) return members;
    return members.filter((m) => String(m.client?.id) === String(exportClientId));
  }, [isAdmin, isBdmScope, members, exportClientId]);

  // One option per person, not per membership — buildUserOptions collapses both lists. A person
  // holds one record per client and the export covers every client they work at (narrowed by the
  // Client filter), so per-client entries produced identical files. The member value is the
  // member record's own id — that is what the download endpoint names as the report owner.
  const { memberOptions, managerOptions, selfOption } = useUserFilterOptions({
    members: scopedMembers,
    managers: subtreeL1Options,
    memberValue: MEMBER_VALUE_MEMBER_ID,
    self: currentManager,
  });

  // The first entry the dropdown actually offers. Seeding from the raw member array instead is
  // what made the default look random (HRMS-280): buildUserOptions sorts each optgroup by label
  // and collapses a person's per-client records into one entry, so members[0] is rarely the row
  // sitting at the top of the list. The fall-through to a Manager option is admin-only on purpose:
  // a manager's own "(Me)" default resolves asynchronously, so offering a manager here would win
  // that race and land the selection on someone else's name instead of their own.
  // @since NEXT_VERSION
  const firstOfferedValue =
    memberOptions[0]?.value ?? (isAdmin ? managerOptions[0]?.value ?? null : null);

  // Option values are strings ("101", "MGR-8"), while a seeded member id arrives as a number, so
  // membership is tested in the option list's own space rather than against the member records.
  //
  // Managers are checked against the offered options exactly as members are. They used to be
  // waved through on the "MGR-" prefix alone, justified by the filename lookups reading an
  // unfiltered superset -- true, but naming was never the bug. A manager the Client filter had
  // dropped stayed selected while vanishing from the dropdown, so the screen showed one person
  // and Export sent another: HRMS-280 again, for managers only (reported 07/09).
  //
  // "(Me)" stays exempt: a manager's own record is always theirs to export, whichever client the
  // filter is showing.
  // @since NEXT_VERSION
  const isOffered = (value) =>
    value === MANAGER_SELF_OPTION_VALUE ||
    (value != null &&
      (memberOptions.some((o) => o.value === String(value)) ||
        managerOptions.some((o) => o.value === String(value))));

  // Resolved during render rather than in an effect, and used for both the dropdown's value and
  // the export request, so the two cannot disagree. Reconciling afterwards left one commit in
  // which the select had already fallen back to showing its first option while the handler still
  // held the person the previous client filter had selected -- an Export clicked in that window
  // produced a report for someone other than the name on screen, which is HRMS-280.
  // @since NEXT_VERSION
  const effectiveMember = isOffered(selectedMember)
    ? selectedMember
    : currentManager
      ? MANAGER_SELF_OPTION_VALUE
      : firstOfferedValue;

  // State follows the resolved value so a later comparison (and the dropdown's own onChange) works
  // from the same selection the user was shown.
  useEffect(() => {
    if (selectedMember !== effectiveMember) {
      setSelectedMember(effectiveMember);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMember]);

  // Stored exactly as the dropdown reports it: a string, like every option value. The selection
  // used to be parsed to a number here while the resolver below wrote strings, so one state held
  // two representations depending on how it got there. The single conversion left is at the
  // request boundary, where the endpoint names a numeric member record.
  // @since NEXT_VERSION
  const onMemberChange = (value) => setSelectedMember(value);

  // A 404 is an entity-not-found from the backend, whose message names internal database ids
  // ("Member does not exists for member id [25]"). That is a server-log detail, not something to
  // put in front of a user, so the banner uses its own wording and the raw body is dropped.
  // Other statuses carry deliberate validation text (an unresolvable scope, for instance), which
  // is written to be read, so those are still surfaced as sent.
  const extractErrorMessage = async (error) => {
    if (error?.response?.status === 404) {
      return "Report not found for the selected member and period.";
    }
    const blob = error?.response?.data;
    if (blob instanceof Blob) {
      try {
        const text = await blob.text();
        const parsed = JSON.parse(text);
        if (parsed?.message) return parsed.message;
      } catch (_) {
        // fall through to generic message
      }
    }
    return error?.message || "Failed to export report. Please try again.";
  };

  // Which client the consolidated workbook covers. An admin picks it in their own section; a
  // manager still picks it with the one shared Client filter, because their Consolidated is a
  // format on the single card rather than a section of its own.
  // @since NEXT_VERSION
  // A manager's Consolidated is a format on their single card, so it reads that card's client. An
  // admin's is a section with a client of its own.
  // Both roles now reach Consolidated through the section, so the section's own client is the
  // scope for either of them.
  const consolidatedScopeClientId = consolidatedClientId;

  // The Consolidated section offers no "All" client, so its value has to name a real client from
  // the moment it renders. A native <select> shows its first option regardless of what state
  // holds, so leaving this empty would display a client the export was not actually scoped to.
  // @since NEXT_VERSION
  useEffect(() => {
    // Both roles reach the section now, so both need its client seeded -- a manager's was left
    // empty, which showed the first option while the request carried no client at all.
    if (!(isManagerScope || isAdmin) || consolidatedClientId) return;
    const firstClientId = clientOptions[0]?.id;
    if (firstClientId != null) setConsolidatedClientId(String(firstClientId));
  }, [isManagerScope, isAdmin, clientOptions, consolidatedClientId]);

  // The scope root the consolidated workbook is expanded from. A manager roots it at their own
  // records. An admin has none, so theirs is rooted at every manager holding a record at the
  // selected client — the widest scope that still describes one client. The owner CTE selects
  // DISTINCT, so overlapping subtrees resolve to one column per person rather than duplicates.
  // @since NEXT_VERSION
  const consolidatedManagerIds = useMemo(() => {
    if (isAdmin) {
      return adminManagers
        .filter((m) => String(m.clientId) === String(consolidatedScopeClientId))
        .map((m) => m.id)
        .filter((id) => id != null);
    }
    // Symmetrical with the admin branch: only the caller's own records at that client root the
    // scope. Sending records from other clients relied on the server filtering them out again.
    return managerClients
      .filter((c) => String(c.clientId) === String(consolidatedScopeClientId))
      .map((c) => c.id)
      .filter((id) => id != null);
  }, [isAdmin, adminManagers, managerClients, consolidatedScopeClientId]);



  // @since NEXT_VERSION
  const isConciseMonthly = format === FORMAT_CONCISE_MONTHLY;

  // develop made Consolidated a section rather than a format, so the person filter is never
  // overridden any more. It shows HRMS-280's resolved selection: the same value the export sends,
  // so the two cannot disagree.
  const userValue = effectiveMember;

  // Mirrors the sample workbook's own name, e.g. "AXT-InOpTra_Timesheet_July 2026.xlsx". The client
  // code is the part of the filter label before the dash. The client and year are passed in rather
  // than read from state, because an admin's Consolidated section keeps its own pair.
  const consolidatedFileName = (calendarMonth, clientId, year) => {
    const label = clientOptions.find((c) => String(c.id) === String(clientId))?.label ?? "";
    const clientCode = label.split(" - ")[0] || String(clientId);
    const monthName = new Date(year, calendarMonth - 1, 1).toLocaleString("en-US", {
      month: "long",
    });
    return `${clientCode}-InOpTra_Timesheet_${monthName} ${year}.xlsx`;
  };

  // One workbook for the whole scope: the scope root is the caller's own manager records (or, for
  // an admin, every manager at the selected client) and the server expands the subtree, so no
  // member list travels with the request.
  const consolidatedClickHandler = () => {
    if (consolidatedFormat !== FORMAT_CONSOLIDATED) {
      // Only one consolidated renderer exists server-side. Refusing an unknown selection here is
      // what makes adding a second report a deliberate change rather than a silent mis-export.
      setErrorMessage("That consolidated report is not available yet.");
      return;
    }
    const managerIds = consolidatedManagerIds;
    if (managerIds.length === 0) {
      setErrorMessage(
        isAdmin
          ? "No managers are assigned to the selected client, so there is no scope to export."
          : "You have no manager record at the selected client, so there is no scope to export."
      );
      return;
    }
    const activePeriod = consolidatedPeriod;
    const clientId = consolidatedScopeClientId;
    const { calendarMonth, year } = exportMonthYear(activePeriod);
    setOpen(true);
    downloadConsolidatedTimesheetHandler(managerIds, clientId, year, calendarMonth)
      .then((result) => {
        if (!result) {
          setOpen(false);
          setErrorMessage("Session expired, please login again.");
          return;
        }
        saveAs(
          new Blob([result.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
          consolidatedFileName(calendarMonth, clientId, year)
        );
        setOpen(false);
      })
      .catch(async (error) => {
        setOpen(false);
        setErrorMessage(await extractErrorMessage(error));
      });
  };

  // The server names this file -- only it knows the resolved client codes and the person's name, and
  // it may span clients, in which case no single client code belongs in the name. Read off
  // Content-Disposition (exposed via CORS for exactly this) rather than built here, so there is one
  // definition of what the file is called. Falls back only if the header is missing.
  // @since NEXT_VERSION
  const filenameFromHeader = (result, fallback) => {
    const header = result?.headers?.["content-disposition"] ?? "";
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
    return match ? decodeURIComponent(match[1]) : fallback;
  };

  // One person per workbook. A manager is a valid selection and simply reports whatever they logged,
  // matching Concise and Details -- this format sits beside them in the same dropdown, so refusing a
  // selection they accept would be a surprise rather than a safeguard.
  // @since NEXT_VERSION
  const conciseMonthlyClickHandler = (ownerId, ownerType, ownerName, calendarMonth, year, clientIds) => {
    setOpen(true);
    downloadConciseMonthlyHandler(ownerId, ownerType, year, calendarMonth, clientIds)
      .then((result) => {
        if (!result) {
          setOpen(false);
          setErrorMessage("Session expired, please login again.");
          return;
        }
        saveAs(
          new Blob([result.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
          filenameFromHeader(result, `${ownerName}-${calendarMonth}-${year}.xlsx`)
        );
        setOpen(false);
      })
      .catch(async (error) => {
        setOpen(false);
        setErrorMessage(await extractErrorMessage(error));
      });
  };

  // The whole organisation, or one department, for one month; the server names the file.
  // @since NEXT_VERSION
  const orgReportClickHandler = () => {
    if (orgFormat !== FORMAT_UTILIZATION) {
      setErrorMessage("That org-level report is not available yet.");
      return;
    }
    const { calendarMonth, year } = exportMonthYear(orgPeriod);
    const monthName = new Date(year, calendarMonth - 1, 1).toLocaleString("en-US", { month: "long" });
    setOpen(true);
    const departmentCode = orgDepartment === ALL_DEPARTMENTS ? null : orgDepartment;
    downloadUtilizationReportHandler(year, calendarMonth, departmentCode)
      .then((result) => {
        if (!result) {
          setOpen(false);
          setErrorMessage("Session expired, please login again.");
          return;
        }
        saveAs(
          new Blob([result.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
          filenameFromHeader(result, `${departmentCode ? `${departmentCode}_` : ""}Utilization_Report_${monthName}${year}.xlsx`)
        );
        setOpen(false);
      })
      .catch(async (error) => {
        setOpen(false);
        setErrorMessage(await extractErrorMessage(error));
      });
  };

  // The member path shares almost nothing with the dropdown path -- no person filter, no
  // manager-self, no L1 manager, no name lookup -- so it is its own handler rather than five
  // negations threaded through the one below.
  // @since NEXT_VERSION
  const memberExportHandler = () => {
    // Pick the record for the selected client; "All" exports every client the person works at,
    // which the server resolves from any one of their record ids.
    const selected =
      exportClientId === ALL_CLIENTS
        ? memberships[0]
        : memberships.find((m) => String(m.client?.id) === String(exportClientId));
    if (!selected) {
      setErrorMessage("Unable to resolve your member profile. Please try again.");
      return;
    }
    const { calendarMonth, year } = exportMonthYear(period);
    const scopedClientIds = resolveScopedClientIds();
    const isLegacy = format === FORMAT_LEGACY;

    setOpen(true);
    downloadTimesheetHandler(selected.id, OWNER_TYPE_MEMBER, year, calendarMonth, isLegacy, scopedClientIds)
      .then((result) => {
        if (!result) {
          setOpen(false);
          setErrorMessage("Session expired, please login again.");
          return;
        }
        const suffix = isLegacy ? "-legacy" : "";
        saveAs(
          new Blob([result.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
          (selected.name ?? "report") + suffix + "-" + calendarMonth + "-" + year + ".xlsx"
        );
        setOpen(false);
      })
      .catch(async (error) => {
        setOpen(false);
        setErrorMessage(await extractErrorMessage(error));
      });
  };

  // "All" normally means "no client filter", which the export endpoints read as every client the
  // person works at. For a BDM that would reach beyond the accounts they own, so their own client
  // ids are sent explicitly instead. Every other scope keeps the previous meaning.
  // @since NEXT_VERSION
  const resolveScopedClientIds = () => {
    if (exportClientId !== ALL_CLIENTS) {
      return [exportClientId];
    }
    if (!isBdmScope) {
      return null;
    }
    return bdmClients.map((client) => client.id).filter((id) => id != null);
  };

  const btnClickHandler = () => {
    if (isMemberScope) {
      memberExportHandler();
      return;
    }
    if (!effectiveMember) {
      setErrorMessage("Please select a member before exporting.");
      return;
    }
    const isManagerSelf = effectiveMember === MANAGER_SELF_OPTION_VALUE;
    // An L1 child picked from the "Managers" optgroup — export that manager's own
    // report via the same manager-scoped handler the "(Me)" option uses.
    const isL1Manager =
      typeof effectiveMember === "string" && effectiveMember.startsWith(MANAGER_OPTION_PREFIX);
    const l1ManagerId = isL1Manager
      ? parseInt(effectiveMember.slice(MANAGER_OPTION_PREFIX.length), 10)
      : null;
    // A member option carries its id as a string; the endpoint names the member record numerically.
    const memberId = isManagerSelf || isL1Manager ? null : parseInt(effectiveMember, 10);
    if (isManagerSelf && !currentManager) {
      setErrorMessage("Unable to resolve your manager profile. Please try again.");
      return;
    }
    // One handler for every combination: the owner's id space and the format are parameters
    // rather than four separate endpoints. member_id and manager_id come from independent
    // sequences whose values overlap, so ownerType is what makes the id unambiguous.
    const isLegacy = format === FORMAT_LEGACY;
    const ownerId = isManagerSelf ? currentManager.id : isL1Manager ? l1ManagerId : memberId;
    const ownerType = isManagerSelf || isL1Manager ? OWNER_TYPE_MANAGER : OWNER_TYPE_MEMBER;

    // The API and the filename both want a calendar month, and Fiscal Week / Custom Dates have to
    // resolve to one. Derived once and used for both — deriving it separately is how the request
    // and the filename came to disagree, so the file said July and contained June.
    const { calendarMonth, year } = exportMonthYear(period);

    // The Client filter now scopes the file, not just the dropdown: "All" exports every client the
    // person works at (matching the consolidated report), a specific client exports just that one.
    const scopedClientIds = resolveScopedClientIds();

    // Concise Monthly branches after the owner and client scope are resolved, so a manager selection
    // behaves exactly as it does for the other per-person formats and "All" spans every client.
    // @since NEXT_VERSION
    if (isConciseMonthly) {
      const managerNames = isAdmin || isBdmScope ? adminManagers : subtreeManagers;
      const ownerName = isManagerSelf
        ? currentManager.name
        : isL1Manager
          ? managerNames.find((m) => String(m.id) === String(l1ManagerId))?.name ?? "report"
          : members.find((m) => m.id === memberId)?.name ?? "report";
      // memberId is HRMS-280's resolved selection; year is HRMS-260's resolved period. Both are
      // needed: the lookup must match the id the request carries, and the finance export takes the
      // year the period picker resolved to rather than the raw Year dropdown.
      conciseMonthlyClickHandler(ownerId, ownerType, ownerName, calendarMonth, year, scopedClientIds);
      return;
    }

    setOpen(true);
    const downloadPromise = downloadTimesheetHandler(
      ownerId,
      ownerType,
      year,
      calendarMonth,
      isLegacy,
      scopedClientIds
    );
    downloadPromise
      .then((result) => {
        if (!result) {
          setOpen(false);
          setErrorMessage("Session expired, please login again.");
          return;
        }
        // Superset used only to resolve the export filename — never client-filtered, so a
        // still-selected manager whose entry the Client filter has dropped from the visible
        // optgroup (subtreeL1Options) still names its file correctly. Admins read the full
        // manager list; managers read their whole subtree.
        // @since NEXT_VERSION
        const managerNameSource = isAdmin || isBdmScope ? adminManagers : subtreeManagers;
        const exportName = isManagerSelf
          ? currentManager.name
          : isL1Manager
            ? managerNameSource.find((m) => String(m.id) === String(l1ManagerId))?.name ?? "report"
            : members.find((m) => m.id === memberId)?.name;
        const suffix = isLegacy ? "-legacy" : "";
        saveAs(
          new Blob([result.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
          exportName + suffix + "-" + calendarMonth + "-" + year + ".xlsx"
        );
        setOpen(false);
      })
      .catch(async (error) => {
        setOpen(false);
        const message = await extractErrorMessage(error);
        setErrorMessage(message);
      });
  };

  // The Report field sits inline with the filters, on the single line each card keeps (Ishan,
  // 02/09). The bar still supports a second row (actionsBelow) for a screen that wants one.
  // @since NEXT_VERSION
  // The reports one person's workbook can be, in the order they are offered. Concise Monthly is
  // admin-only: the finance report goes outside the company, so who may produce one is a role
  // question rather than a scope question. A manager still exports their own and their members'
  // timesheets through Concise and Details.
  // @since NEXT_VERSION
  const individualFormatOptions = [
    { value: FORMAT_LEGACY, label: "Concise" },
    ...(isAdmin ? [{ value: FORMAT_CONCISE_MONTHLY, label: "Concise Monthly - 1 (Fin)" }] : []),
    { value: FORMAT_DETAILED, label: "Details" },
  ];

  const formatField = (
    <ReportFormatSelect
      id="format"
      value={format}
      onChange={setFormat}
      options={individualFormatOptions}
    />
  );

  // The consolidated section's own Report field. One entry today -- AXTech is the only
  // consolidated workbook the server can render -- and it is where the report's name now lives,
  // since Consolidated is a section rather than an entry in the individual list. A second
  // consolidated report needs a renderer server-side before it can be offered here.
  // @since NEXT_VERSION
  // One consolidated workbook exists today. A second renderer server-side becomes one more entry
  // here and nothing else changes.
  // @since NEXT_VERSION
  const consolidatedFormatOptions = [
    { value: FORMAT_CONSOLIDATED, label: "Consolidated (AXTech)" },
  ];

  const consolidatedFormatField = (
    <ReportFormatSelect
      id="consolidated-format"
      value={consolidatedFormat}
      onChange={setConsolidatedFormat}
      options={consolidatedFormatOptions}
    />
  );

  // Department codes for now, alphabetical, without the departments that log no timesheets.
  // @since NEXT_VERSION
  const departmentOptions = useMemo(
    () =>
      departments
        .filter((d) => d?.code)
        .filter(
          (d) => !DEPARTMENTS_WITHOUT_TIMESHEETS.includes(String(d.displayName ?? "").trim().toLowerCase())
        )
        .map((d) => ({ id: d.code, label: d.code }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [departments]
  );

  const orgDepartmentField = (
    <ClientFilterSelect
      id="export-org-department"
      label="Department"
      value={orgDepartment}
      onChange={setOrgDepartment}
      options={departmentOptions}
    />
  );

  // One org-level report today; a further one becomes another entry here.
  // @since NEXT_VERSION
  const orgFormatField = (
    <ReportFormatSelect
      id="org-format"
      label="Report Type"
      value={orgFormat}
      onChange={setOrgFormat}
      options={[{ value: FORMAT_UTILIZATION, label: "Utilization Report" }]}
    />
  );

  return (
    <Box
      sx={{
        width: "100%",
        mt: 5,
        px: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={1}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
        >
          <MDTypography variant="h6" color="white">
            Export Individual Reports
          </MDTypography>
        </MDBox>
        <MDBox mt={3} p={2}>
          {/* No "All" on the person filter: an export is one person's timesheet, so there
              is always a selection. Format is export-only and sits before the button.
              A member gets a Client filter but no person filter: they export only themselves, at
              one of their own accounts or all of them. The Export button stays disabled until
              their records resolve, so the failure shows before the click rather than after.
              @since NEXT_VERSION */}
          <EffortFilterBar
            idPrefix="export"
            showClient={isManagerScope || isAdmin || isMemberScope || isBdmScope}
            clientValue={exportClientId}
            onClientChange={setExportClientId}
            clientOptions={clientOptions}
            showMemberManagerDropdown={!isMemberScope}
            userValue={userValue}
            onUserChange={onMemberChange}
            memberOptions={memberOptions}
            managerOptions={managerOptions}
            selfOption={selfOption}
            showAllMemberManagerOption={false}
            period={period}
            onPeriodChange={setPeriod}
            showOnlyMonthPeriod
            actionLabel="Export"
            onAction={btnClickHandler}
            actionDisabled={isMemberScope && memberships.length === 0}
          >
            {formatField}
          </EffortFilterBar>
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={open}>
            <CircularProgress color="inherit" />
          </Backdrop>
        </MDBox>
      </Card>

      {/* Second section, for managers and admins alike. Consolidated covers one client's whole
          scope, so it offers no "All" client and no person filter at all -- the two things that
          would make the workbook ambiguous. A manager's scope is rooted at their own manager
          records, an admin's at every manager holding a record at the chosen client.
          @since NEXT_VERSION */}
      {(isManagerScope || isAdmin) && (
        <Card sx={{ mt: 6 }}>
          <MDBox
            mx={2}
            mt={-3}
            py={1}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              Export Consolidated Reports
            </MDTypography>
          </MDBox>
          <MDBox mt={3} p={2}>
            <EffortFilterBar
              idPrefix="export-consolidated"
              showClient
              clientValue={consolidatedClientId}
              onClientChange={setConsolidatedClientId}
              clientOptions={clientOptions}
              showAllClientOption={false}
              showMemberManagerDropdown={false}
              userValue={consolidatedUserValue}
              onUserChange={setConsolidatedUserValue}
              period={consolidatedPeriod}
              onPeriodChange={setConsolidatedPeriod}
              showOnlyMonthPeriod
              actionLabel="Export"
              onAction={consolidatedClickHandler}
            >
              {consolidatedFormatField}
            </EffortFilterBar>
          </MDBox>
        </Card>
      )}

      {/* Third section, admins only (HRMS-319). Org-level reports cover every person at every
          client, narrowed only by department and period. @since NEXT_VERSION */}
      {isAdmin && (
        <Card sx={{ mt: 6 }}>
          <MDBox
            mx={2}
            mt={-3}
            py={1}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              Org-level Reports
            </MDTypography>
          </MDBox>
          <MDBox mt={3} p={2}>
            <EffortFilterBar
              idPrefix="export-org"
              leadingFilters={orgDepartmentField}
              showMemberManagerDropdown={false}
              period={orgPeriod}
              onPeriodChange={setOrgPeriod}
              showOnlyMonthPeriod
              actionLabel="Export"
              onAction={orgReportClickHandler}
            >
              {orgFormatField}
            </EffortFilterBar>
          </MDBox>
        </Card>
      )}
      <Snackbar
        open={Boolean(errorMessage)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={8000}
        onClose={() => setErrorMessage("")}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setErrorMessage("")}
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExportReportsView;
